/**
 * DuplicateValidation Component
 * 
 * Shows error if reward is already assigned to user/partner combination.
 * Prevents form submission when duplicate is detected.
 */

import React from 'react';
import { useOfferDuplicateValidation } from '@/app/hooks/useOfferDuplicateValidation';
import './DuplicateValidation.scss';

interface DuplicateValidationProps {
  userId: string;
  partnerProductId: number;
  rewardId: string;
}

export const DuplicateValidation: React.FC<DuplicateValidationProps> = ({
  userId,
  partnerProductId,
  rewardId,
}) => {
  const { isDuplicate, existingOffer, isLoading, error } = useOfferDuplicateValidation(
    userId,
    partnerProductId,
    rewardId
  );

  if (isLoading) {
    return (
      <div className="duplicate-validation loading">
        <span className="spinner-small"></span> Validating reward assignment...
      </div>
    );
  }

  if (error) {
    return (
      <div className="duplicate-validation error">
        <span className="error-icon">⚠️</span> {error}
      </div>
    );
  }

  if (!isDuplicate) {
    return null;
  }

  return (
    <div className="duplicate-validation duplicate-error">
      <div className="error-header">
        <span className="error-icon">❌</span>
        <strong>Duplicate Reward Assignment</strong>
      </div>
      <div className="error-content">
        <p className="error-message">
          This reward is already assigned to this user/partner combination.
        </p>
        {existingOffer && (
          <div className="existing-offer-details">
            <div className="detail-row">
              <span className="label">Existing Offer ID:</span>
              <span className="value mono">{existingOffer.offer_id}</span>
            </div>
            <div className="detail-row">
              <span className="label">Priority:</span>
              <span className="value">{existingOffer.display_priority}</span>
            </div>
            <div className="detail-row">
              <span className="label">Created:</span>
              <span className="value">
                {new Date(existingOffer.created_at).toLocaleDateString()}
              </span>
            </div>
          </div>
        )}
        <p className="error-instruction">
          Please select a different reward or edit the existing offer instead.
        </p>
      </div>
    </div>
  );
};

export default DuplicateValidation;

